import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import type {CSSProperties, RefObject} from "react";
import {Controller, useFieldArray, useFormContext} from "react-hook-form";
import {Button, Col, Input, Row, Select, Space, Switch} from "antd";
import {
  DeleteOutlined,
  DownOutlined,
  PlusCircleFilled,
  UpOutlined,
} from "@ant-design/icons";
import {usePricingCurrencies} from "../../../../features/currencies/hooks/usePricingCurrencies";
import {SubscriptionTiersFormValues} from "./types";
import {CustomInputComponent} from "../../../input/input-component";
import removeCurrencyIcon from "../../../../assets/remove-currency.png";
import {CardRadioGroup} from "../../../shared/card-radio-group";
import {AutomaticCurrencyConversionNotice} from "../../../shared/automatic-currency-conversion";
import {TierRedirectAfterPurchase} from "./tier-redirect-after-purchase";
import {IS_PWYW_ENABLED} from "../../../../services/config";
import type {OfferingFormPriceType} from "@/features/pricing";
import {FixedPwywCurrenciesFormField} from "../../general/pricing/pwyw-currencies";
import {
  isChargedTier,
  toCurrencyConfigRows,
  transitionTierCurrencyRows,
} from "./tier-currency-rows";
import {
  PWYW_CARD_DESCRIPTION,
  PWYW_CARD_TITLE,
} from "../../general/pricing/pricing-model-cards";

/** Matches dashboard mobile header / main offset; see dashboard-wrapper.style.ts */
const DASHBOARD_MOBILE_MAIN_TOP_INSET_PX = 58.4;

/** Tier that defines the manual currency columns for every tier (first charged tier, else 0). */
function getSubscriptionCurrencyDriverTierIndex(
  tiers: {priceType?: string}[] | undefined
): number {
  if (!tiers?.length) {
    return 0;
  }
  const firstChargedIdx = tiers.findIndex((t) => isChargedTier(t.priceType));
  return firstChargedIdx >= 0 ? firstChargedIdx : 0;
}

export function SubscriptionTierFormFields() {
  const {control, watch, setValue, clearErrors} =
    useFormContext<SubscriptionTiersFormValues>();
  const {formCurrencies: pricingFormCurrencies} = usePricingCurrencies();

  const {
    fields: tierFields,
    append: appendTier,
    remove: removeTier,
  } = useFieldArray({
    control,
    name: "tiers",
  });

  const formCurrencies =
    pricingFormCurrencies.length > 0 ? pricingFormCurrencies : ["NGN"];
  const defaultCurrency = formCurrencies[0] ?? "NGN";
  const watchedTiers = watch("tiers");
  const tiers = useMemo(() => watchedTiers ?? [], [watchedTiers]);
  const currencyDriverTierIndex = useMemo(
    () => getSubscriptionCurrencyDriverTierIndex(tiers),
    [tiers]
  );
  const mainTierCurrencyCodes = useMemo(() => {
    const driverCurrencies = tiers[currencyDriverTierIndex]?.currencies ?? [];
    return driverCurrencies.reduce<string[]>((currencyCodes, currency) => {
      if (currency?.callPriceCurrency) {
        currencyCodes.push(currency.callPriceCurrency);
      }
      return currencyCodes;
    }, []);
  }, [tiers, currencyDriverTierIndex]);
  const firstMainTierCurrency = mainTierCurrencyCodes[0] ?? defaultCurrency;
  const configuredTierCurrencies =
    tiers[currencyDriverTierIndex]?.currencies?.length > 0
      ? tiers[currencyDriverTierIndex].currencies.map((currency) => ({
          callPriceCurrency: currency.callPriceCurrency,
          callPrice: 0,
        }))
      : [{callPriceCurrency: firstMainTierCurrency, callPrice: 0}];
  const tiersCurrencyKey = useMemo(
    () => JSON.stringify(tiers.map((tier) => tier?.currencies ?? [])),
    [tiers]
  );
  const supportedCurrencyKey = useMemo(
    () => JSON.stringify(mainTierCurrencyCodes),
    [mainTierCurrencyCodes]
  );
  const hasChargedTier = tiers.some((tier) => isChargedTier(tier?.priceType));
  const tierSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (tierFields.length === 0) {
      appendTier({
        tierName: "",
        priceType: "paid",
        currencies: [{callPriceCurrency: defaultCurrency, callPrice: 0}],
        billingFrequency: "monthly",
        billingCount: "until_cancel",
        featuresEnabled: false,
        features: [],
        redirectAfterPurchaseEnabled: false,
      });
    }
  }, [tierFields.length, defaultCurrency, appendTier]);

  useEffect(() => {
    if (mainTierCurrencyCodes.length === 0) {
      return;
    }

    tiers.forEach((tier, tierIndex) => {
      if (tierIndex === currencyDriverTierIndex) {
        return;
      }
      const nextCurrencies = toCurrencyConfigRows(tier, mainTierCurrencyCodes);

      if (JSON.stringify(tier?.currencies ?? []) !== JSON.stringify(nextCurrencies)) {
        setValue(`tiers.${tierIndex}.currencies`, nextCurrencies, {
          shouldDirty: true,
          shouldValidate: false,
        });
        clearErrors(`tiers.${tierIndex}.currencies`);
      }
    });
  }, [
    tiers,
    tiersCurrencyKey,
    mainTierCurrencyCodes,
    supportedCurrencyKey,
    currencyDriverTierIndex,
    setValue,
    clearErrors,
  ]);

  return (
    <div
      ref={tierSectionRef}
      className="subscription-tier-form-mobile-safe"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 24,
      }}
    >
      <p style={{color: "#1C1726", margin: 0, fontSize: "14px"}}>
        Create subscription tiers that offer your customers different payment options.
      </p>

      {hasChargedTier ? (
        <SubscriptionCurrencyConfig
          key={currencyDriverTierIndex}
          tierSectionElementRef={tierSectionRef}
          driverTierIndex={currencyDriverTierIndex}
        />
      ) : null}

      {tierFields.map((tier, tierIndex) => (
        <TierFormFields
          key={tier.id}
          tierIndex={tierIndex}
          removeTier={() => {
            if (tierFields.length > 1 && tierIndex > 0) {
              removeTier(tierIndex);
            }
          }}
          canRemove={tierFields.length > 1 && tierIndex > 0}
        />
      ))}

      <Button
        icon={<PlusCircleFilled />}
        onClick={() =>
          appendTier({
            tierName: "",
            priceType: "paid",
            currencies: configuredTierCurrencies,
            billingFrequency: "monthly",
            billingCount: "until_cancel",
            featuresEnabled: false,
            features: [],
            redirectAfterPurchaseEnabled: false,
          })
        }
        size="small"
        style={{alignSelf: "flex-start", width: "auto"}}
      >
        Add tier
      </Button>

      {hasChargedTier ? <SubscriptionCurrencyConversionNotice /> : null}
    </div>
  );
}

/** Subscription-level automatic conversion; uses the currency-driver tier (first paid) for base list. */
function SubscriptionCurrencyConversionNotice() {
  const {watch} = useFormContext<SubscriptionTiersFormValues>();
  const tiers = watch("tiers") ?? [];
  const driverIdx = getSubscriptionCurrencyDriverTierIndex(tiers);
  const sourceCurrency = tiers[driverIdx]?.currencies?.[0]?.callPriceCurrency;

  if (!sourceCurrency) {
    return null;
  }

  return <AutomaticCurrencyConversionNotice sourceCurrency={sourceCurrency} />;
}

function SubscriptionCurrencyConfig({
  tierSectionElementRef,
  driverTierIndex,
}: {
  tierSectionElementRef: RefObject<HTMLDivElement | null>;
  driverTierIndex: number;
}) {
  const {control, setValue, watch, getValues, clearErrors} =
    useFormContext<SubscriptionTiersFormValues>();
  const {formCurrencies: pricingFormCurrencies} = usePricingCurrencies();
  const formCurrencies =
    pricingFormCurrencies.length > 0 ? pricingFormCurrencies : ["NGN"];
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [viewportPinned, setViewportPinned] = useState(false);
  const [spacerHeight, setSpacerHeight] = useState(0);
  const [fixedBox, setFixedBox] = useState({top: 0, left: 0, width: 0});
  const {
    fields: currencyFields,
    append: appendCurrency,
    remove: removeCurrency,
  } = useFieldArray({
    control,
    name: `tiers.${driverTierIndex}.currencies`,
  });
  const selectedCurrencies = watch(`tiers.${driverTierIndex}.currencies`) ?? [];
  const selectedCurrencyCodes = selectedCurrencies
    .map((currency) => currency.callPriceCurrency)
    .filter(Boolean);
  const availableCurrencies = formCurrencies.filter(
    (currency) =>
      !selectedCurrencyCodes.some(
        (selectedCurrency) => selectedCurrency.toUpperCase() === currency.toUpperCase()
      )
  );

  const syncConfiguredCurrenciesToOtherTiers = useCallback(
    (nextCurrencies: {callPriceCurrency: string}[]) => {
      const snapshotTiers = getValues("tiers") ?? [];
      snapshotTiers.forEach((tier, tierIndex) => {
        if (tierIndex === driverTierIndex) {
          return;
        }
        const syncedCurrencies = toCurrencyConfigRows(
          tier,
          nextCurrencies.map((currency) => currency.callPriceCurrency)
        );

        setValue(`tiers.${tierIndex}.currencies`, syncedCurrencies, {
          shouldDirty: true,
          shouldValidate: false,
        });
        clearErrors(`tiers.${tierIndex}.currencies`);
      });
    },
    [driverTierIndex, getValues, setValue, clearErrors]
  );

  const updateViewportPinLayout = useCallback(() => {
    const main = document.getElementById("main-section-container");
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!main || !anchor || !panel) {
      return;
    }

    const sectionEl = tierSectionElementRef.current;
    const fallbackColumn = main.firstElementChild as HTMLElement | null;
    const boundsEl = sectionEl ?? fallbackColumn;
    if (!boundsEl) {
      return;
    }

    const isMobileDashboard = window.matchMedia("(max-width: 960px)").matches;
    const topInset = isMobileDashboard ? DASHBOARD_MOBILE_MAIN_TOP_INSET_PX : 0;
    const anchorTop = anchor.getBoundingClientRect().top;
    const tierSectionBottom =
      sectionEl?.getBoundingClientRect().bottom ?? Number.POSITIVE_INFINITY;
    const nextPinned = anchorTop < topInset && tierSectionBottom > topInset;
    const {left, width} = boundsEl.getBoundingClientRect();

    setFixedBox({top: topInset, left, width});

    if (nextPinned) {
      setSpacerHeight(panel.offsetHeight);
    } else {
      setSpacerHeight(0);
    }

    setViewportPinned(nextPinned);
  }, [tierSectionElementRef]);

  useLayoutEffect(() => {
    const main = document.getElementById("main-section-container");
    if (!main) {
      return;
    }

    updateViewportPinLayout();
    main.addEventListener("scroll", updateViewportPinLayout, {passive: true});
    window.addEventListener("resize", updateViewportPinLayout);

    const mediaQuery = window.matchMedia("(max-width: 960px)");
    mediaQuery.addEventListener("change", updateViewportPinLayout);

    return () => {
      main.removeEventListener("scroll", updateViewportPinLayout);
      window.removeEventListener("resize", updateViewportPinLayout);
      mediaQuery.removeEventListener("change", updateViewportPinLayout);
    };
  }, [updateViewportPinLayout]);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(() => {
      updateViewportPinLayout();
    });
    observer.observe(panel);
    return () => observer.disconnect();
  }, [updateViewportPinLayout]);

  useLayoutEffect(() => {
    updateViewportPinLayout();
  }, [currencyFields.length, driverTierIndex, updateViewportPinLayout]);

  const panelFixedStyle: CSSProperties = viewportPinned
    ? {
        position: "fixed",
        top: fixedBox.top,
        left: fixedBox.left,
        width: fixedBox.width,
        maxWidth: fixedBox.width,
        zIndex: 92,
        boxSizing: "border-box",
      }
    : {width: "100%", maxWidth: "100%", boxSizing: "border-box"};

  return (
    <div ref={anchorRef} style={{width: "100%"}}>
      {spacerHeight > 0 ? (
        <div style={{height: spacerHeight, flexShrink: 0}} aria-hidden />
      ) : null}
      <div
        ref={panelRef}
        className="subscription-currency-config-sticky"
        style={panelFixedStyle}
      >
        <Space direction="vertical" size={12} style={{display: "flex", width: "100%"}}>
          <div style={{display: "flex", flexWrap: "wrap", columnGap: 12, rowGap: 8}}>
            {currencyFields.map((field, index) => {
              const currentCurrency = selectedCurrencies[index]?.callPriceCurrency;
              const options = Array.from(
                new Set([currentCurrency, ...availableCurrencies].filter(Boolean))
              );

              return (
                <div key={field.id}>
                  <div style={{display: "flex", alignItems: "center", gap: 4}}>
                    <Controller
                      control={control}
                      name={`tiers.${driverTierIndex}.currencies.${index}.callPriceCurrency`}
                      render={({field: currencyField}) => (
                        <Select
                          {...currencyField}
                          className="subscription-currency-config-select"
                          dropdownClassName="subscription-currency-config-dropdown-mobile"
                          size="small"
                          style={{
                            width: 74,
                            backgroundColor: "#fff",
                            borderRadius: 9999,
                          }}
                          options={options.map((currency) => ({
                            label: currency,
                            value: currency,
                          }))}
                          onChange={(newCurrency) => {
                            const prevRow =
                              getValues(`tiers.${driverTierIndex}.currencies`) ?? [];
                            const nextDriverCurrencies = prevRow.map((entry, idx) =>
                              idx === index
                                ? {
                                    callPriceCurrency: newCurrency as string,
                                    callPrice: Number(entry?.callPrice) || 0,
                                  }
                                : {
                                    callPriceCurrency: entry?.callPriceCurrency ?? "",
                                    callPrice: Number(entry?.callPrice) || 0,
                                  }
                            );
                            currencyField.onChange(newCurrency);
                            syncConfiguredCurrenciesToOtherTiers(nextDriverCurrencies);
                          }}
                        />
                      )}
                    />
                    {index > 0 ? (
                      <Button
                        type="text"
                        size="small"
                        style={{
                          border: "none",
                          boxShadow: "none",
                          height: 20,
                          minWidth: 20,
                          padding: 0,
                          width: 20,
                        }}
                        onClick={() => {
                          const nextCurrencies = selectedCurrencies.filter(
                            (_, currencyIndex) => currencyIndex !== index
                          );
                          removeCurrency(index);
                          syncConfiguredCurrenciesToOtherTiers(nextCurrencies);
                        }}
                        aria-label="Remove currency"
                      >
                        <img
                          alt=""
                          src={removeCurrencyIcon}
                          style={{display: "block", height: 12, width: 12}}
                        />
                      </Button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
          <Button
            icon={<PlusCircleFilled />}
            onClick={() => {
              const nextCurrency = availableCurrencies[0];
              if (nextCurrency) {
                const nextCurrencies = [
                  ...selectedCurrencies,
                  {callPriceCurrency: nextCurrency, callPrice: 0},
                ];
                appendCurrency(
                  toCurrencyConfigRows(
                    {
                      priceType: getValues(`tiers.${driverTierIndex}.priceType`),
                      currencies: [],
                    },
                    [nextCurrency]
                  )[0]
                );
                syncConfiguredCurrenciesToOtherTiers(nextCurrencies);
              }
            }}
            size="small"
            disabled={availableCurrencies.length === 0}
            type="text"
            style={{border: "none", width: "fit-content", padding: 0}}
          >
            Add a currency
          </Button>
        </Space>
      </div>
    </div>
  );
}

function TierSummaryText({tierIndex}: {tierIndex: number}) {
  const {watch} = useFormContext<SubscriptionTiersFormValues>();
  const currencies = watch(`tiers.${tierIndex}.currencies`) ?? [];
  const billingFrequency = watch(`tiers.${tierIndex}.billingFrequency`) ?? "monthly";
  const billingCount = watch(`tiers.${tierIndex}.billingCount`) ?? "until_cancel";
  const billingCycles = watch(`tiers.${tierIndex}.billingCycles`);
  const priceType = watch(`tiers.${tierIndex}.priceType`) ?? "paid";

  const currency = currencies[0]?.callPriceCurrency ?? "NGN";
  // Under Pay What You Want the tier is summed up by its floor, shown as "NGN 500+".
  const price =
    (priceType === "pwyw" ? currencies[0]?.minCallPrice : currencies[0]?.callPrice) ?? 0;
  const parsedPrice = Number(price);
  const formattedPrice = `${currency} ${
    Number.isFinite(parsedPrice) ? parsedPrice.toLocaleString() : 0
  }${priceType === "pwyw" ? "+" : ""}`;
  const parsedBillingCycles = Number(billingCycles);
  const billingCyclesText =
    Number.isFinite(parsedBillingCycles) && parsedBillingCycles > 0
      ? parsedBillingCycles
      : 0;

  const frequencyText =
    billingFrequency === "daily"
      ? "daily"
      : billingFrequency === "weekly"
      ? "weekly"
      : billingFrequency === "yearly"
      ? "yearly"
      : "monthly";
  const frequencyUnit =
    billingFrequency === "daily"
      ? "day"
      : billingFrequency === "weekly"
      ? "week"
      : billingFrequency === "yearly"
      ? "year"
      : "month";
  const customBillingText = `for ${billingCyclesText} ${frequencyUnit}${
    billingCyclesText === 1 ? "" : "s"
  }`;
  const billingText =
    billingCount === "until_cancel" ? "until they decide to cancel" : customBillingText;

  const summaryText =
    priceType === "free"
      ? "Your customers will get free access to this tier."
      : `Your customers will pay ${formattedPrice} ${frequencyText} ${billingText}.`;

  return (
    <div
      style={{
        color: "#002421",
        backgroundColor: "#E1F6F5",
        border: "1px solid #50D3CD",
        borderRadius: 5,
        padding: "9px 16px",
      }}
    >
      {summaryText}
    </div>
  );
}

function TierPricesFormField({tierIndex}: {tierIndex: number}) {
  const {
    control,
    watch,
    formState: {errors},
  } = useFormContext<SubscriptionTiersFormValues>();
  const currencies = watch(`tiers.${tierIndex}.currencies`) ?? [];
  const currencyErrors = errors?.tiers?.[tierIndex]?.currencies;

  return (
    <Row gutter={[10, 24]}>
      {currencies.map((currency, index) => (
        <Col key={`${currency.callPriceCurrency}-${index}`} span={24} sm={12}>
          <CustomInputComponent
            id={`tier-${tierIndex}-price-${index}`}
            name={`tiers.${tierIndex}.currencies.${index}.callPrice`}
            type="text"
            label={index === 0 ? "Default price" : "Other price"}
            placeholder="100"
            error={currencyErrors?.[index]?.callPrice?.message}
            control={control}
            addonBefore={
              <span style={{minWidth: 48, display: "inline-block"}}>
                {currency.callPriceCurrency}
              </span>
            }
          />
        </Col>
      ))}
    </Row>
  );
}

function TierFormFields({
  tierIndex,
  removeTier,
  canRemove,
}: {
  tierIndex: number;
  removeTier: () => void;
  canRemove: boolean;
}) {
  const {
    control: formControl,
    watch,
    unregister,
    setValue,
    getValues,
    clearErrors,
    formState: {errors},
  } = useFormContext<SubscriptionTiersFormValues>();

  const tierFeaturesErrorMessage =
    (errors?.tiers?.[tierIndex]?.features as {message?: string} | undefined)?.message ??
    "";
  const {
    fields: featureFields,
    append: appendFeature,
    remove: removeFeature,
  } = useFieldArray({
    control: formControl,
    name: `tiers.${tierIndex}.features`,
  });

  const featuresEnabled = watch(`tiers.${tierIndex}.featuresEnabled`);
  const redirectAfterPurchaseEnabled = watch(
    `tiers.${tierIndex}.redirectAfterPurchaseEnabled`
  );
  const billingCount = watch(`tiers.${tierIndex}.billingCount`);
  const priceType = watch(`tiers.${tierIndex}.priceType`) ?? "paid";
  const [isCollapsed, setIsCollapsed] = useState(false);

  /**
   * Moves the tier to another Pricing Model, carrying its rows across first so the new
   * model's fields open on the right amounts.
   */
  function applyPricingModel(
    from: OfferingFormPriceType | undefined,
    to: OfferingFormPriceType,
    setPriceType: (priceType: OfferingFormPriceType) => void
  ) {
    clearErrors(`tiers.${tierIndex}.currencies`);
    setValue(
      `tiers.${tierIndex}.currencies`,
      transitionTierCurrencyRows(
        from ?? "paid",
        to,
        getValues(`tiers.${tierIndex}.currencies`) ?? []
      )
    );
    setPriceType(to);
  }

  function handleAddFeature() {
    appendFeature({id: `f-${Date.now()}`, value: ""});
  }

  const whiteContentStyle: CSSProperties = {
    backgroundColor: "#fff",
    borderRadius: 8,
    padding: "1.6rem",
  };

  const greyWrapperStyle: CSSProperties = {
    backgroundColor: "#fafafa",
    padding: "1.6rem",
    display: "flex",
    flexDirection: "column",
    gap: 16,
  };

  const compactToggleButtonStyle: CSSProperties = {
    width: 24,
    minWidth: 24,
    height: 24,
    padding: 0,
    fontSize: 10,
    lineHeight: "24px",
  };

  const compactToggleIconStyle: CSSProperties = {
    fontSize: 10,
  };

  const compactTierRemoveButtonStyle: CSSProperties = {
    width: 22,
    minWidth: 22,
    height: 22,
    padding: 0,
    lineHeight: "22px",
  };

  const compactTierRemoveIconStyle: CSSProperties = {
    fontSize: 12,
  };

  return (
    <div
      style={{
        border: "1px solid #f0f0f0",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <div style={{...whiteContentStyle, borderBottom: "1px solid #f0f0f0"}}>
        <Controller
          control={formControl}
          name={`tiers.${tierIndex}.tierName`}
          render={({field, fieldState: {error}}) => (
            <div>
              <label style={{display: "block", marginBottom: 8, fontWeight: 500}}>
                Tier name
              </label>
              <div style={{display: "flex", alignItems: "center", gap: 8}}>
                <Input
                  {...field}
                  size="large"
                  placeholder="e.g. Basic Tier"
                  status={error?.message ? "error" : ""}
                />
                <Button
                  type="text"
                  shape="circle"
                  icon={
                    isCollapsed ? (
                      <DownOutlined style={compactToggleIconStyle} />
                    ) : (
                      <UpOutlined style={compactToggleIconStyle} />
                    )
                  }
                  style={compactToggleButtonStyle}
                  onClick={() => setIsCollapsed((current) => !current)}
                  aria-label={isCollapsed ? "Expand tier" : "Collapse tier"}
                />
                {canRemove ? (
                  <Button
                    type="text"
                    shape="circle"
                    size="small"
                    icon={<DeleteOutlined style={compactTierRemoveIconStyle} />}
                    onClick={removeTier}
                    aria-label="Delete tier"
                    style={compactTierRemoveButtonStyle}
                  />
                ) : null}
              </div>
            </div>
          )}
        />
      </div>

      {!isCollapsed ? (
        <div style={greyWrapperStyle}>
          <div style={whiteContentStyle}>
            <Space
              direction="vertical"
              size={24}
              style={{display: "flex", width: "100%"}}
            >
              <Controller
                control={formControl}
                name={`tiers.${tierIndex}.priceType`}
                render={({field: {onChange, value, name, onBlur, ref}}) => (
                  <CardRadioGroup
                    value={value}
                    onChange={(selected) =>
                      applyPricingModel(
                        value,
                        selected as OfferingFormPriceType,
                        onChange
                      )
                    }
                    name={name}
                    onBlur={onBlur}
                    ref={ref}
                    stackOnMobile
                    options={[
                      {
                        value: "free",
                        title: "Free",
                        description: "Give customers free access",
                      },
                      {
                        value: "paid",
                        title: "Set a price",
                        description: "Charge a recurring fee",
                      },
                      ...(IS_PWYW_ENABLED
                        ? [
                            {
                              value: "pwyw",
                              title: PWYW_CARD_TITLE,
                              description: PWYW_CARD_DESCRIPTION,
                            },
                          ]
                        : []),
                    ]}
                  />
                )}
              />

              {priceType === "paid" ? (
                <TierPricesFormField tierIndex={tierIndex} />
              ) : null}

              {IS_PWYW_ENABLED && priceType === "pwyw" ? (
                <FixedPwywCurrenciesFormField name={`tiers.${tierIndex}.currencies`} />
              ) : null}

              {isChargedTier(priceType) ? (
                <>
                  <div>
                    <Controller
                      control={formControl}
                      name={`tiers.${tierIndex}.billingFrequency`}
                      render={({field}) => (
                        <>
                          <label
                            style={{display: "block", marginBottom: 8, fontWeight: 500}}
                          >
                            Billing frequency
                          </label>
                          <Select
                            {...field}
                            size="large"
                            style={{width: "100%"}}
                            options={[
                              {label: "Daily", value: "daily"},
                              {label: "Weekly", value: "weekly"},
                              {label: "Monthly", value: "monthly"},
                              {label: "Yearly", value: "yearly"},
                            ]}
                          />
                        </>
                      )}
                    />
                  </div>
                  <div>
                    <Controller
                      control={formControl}
                      name={`tiers.${tierIndex}.billingCount`}
                      render={({field}) => (
                        <>
                          <label
                            style={{display: "block", marginBottom: 8, fontWeight: 500}}
                          >
                            Number of times your customers will be billed for this
                            subscription
                          </label>
                          <Select
                            {...field}
                            size="large"
                            style={{width: "100%"}}
                            options={[
                              {label: "Bill till they cancel", value: "until_cancel"},
                              {label: "Custom", value: "custom"},
                            ]}
                          />
                        </>
                      )}
                    />
                  </div>
                  {billingCount === "custom" ? (
                    <div>
                      <Controller
                        control={formControl}
                        name={`tiers.${tierIndex}.billingCycles`}
                        render={({field, fieldState: {error}}) => (
                          <>
                            <label
                              style={{display: "block", marginBottom: 8, fontWeight: 500}}
                            >
                              Set number of times
                            </label>
                            <Input
                              {...field}
                              size="large"
                              type="number"
                              min={1}
                              placeholder="12"
                              status={error?.message ? "error" : ""}
                            />
                            {error?.message ? (
                              <div
                                style={{fontSize: "1.4rem", color: "#ff4d4f"}}
                                role="alert"
                              >
                                {error.message}
                              </div>
                            ) : null}
                          </>
                        )}
                      />
                    </div>
                  ) : null}
                </>
              ) : null}

              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <label style={{fontWeight: 500}}>Features</label>
                  <Controller
                    control={formControl}
                    name={`tiers.${tierIndex}.featuresEnabled`}
                    render={({field}) => (
                      <Switch
                        {...field}
                        checked={Boolean(field.value)}
                        onChange={(checked) => {
                          field.onChange(checked);
                          if (checked && featureFields.length === 0) {
                            appendFeature({id: `f-${Date.now()}`, value: ""});
                          }
                        }}
                      />
                    )}
                  />
                </div>
                {featuresEnabled && (
                  <Space direction="vertical" size={12} style={{display: "flex"}}>
                    <p style={{color: "#8c8c8c", margin: 0, fontSize: 13}}>
                      List what's included in this tier
                    </p>
                    {tierFeaturesErrorMessage ? (
                      <div style={{fontSize: "1.4rem", color: "#ff4d4f"}} role="alert">
                        {tierFeaturesErrorMessage}
                      </div>
                    ) : null}
                    {featureFields.map((feature, featureIndex) => (
                      <div
                        key={feature.id}
                        style={{display: "flex", alignItems: "center", gap: 8}}
                      >
                        <Controller
                          control={formControl}
                          name={`tiers.${tierIndex}.features.${featureIndex}.value`}
                          render={({field}) => (
                            <Input
                              {...field}
                              size="large"
                              placeholder="e.g Access to private group chat"
                              status={tierFeaturesErrorMessage ? "error" : undefined}
                            />
                          )}
                        />
                        {featureIndex > 0 ? (
                          <Button
                            type="text"
                            shape="circle"
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => removeFeature(featureIndex)}
                            aria-label="Delete feature"
                          />
                        ) : null}
                      </div>
                    ))}
                    <Button
                      icon={<PlusCircleFilled />}
                      onClick={handleAddFeature}
                      size="small"
                      style={{border: "none"}}
                    >
                      Add a feature
                    </Button>
                  </Space>
                )}
              </div>

              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: redirectAfterPurchaseEnabled ? 16 : 0,
                  }}
                >
                  <label style={{fontWeight: 500}}>Redirect after purchase</label>
                  <Controller
                    control={formControl}
                    name={`tiers.${tierIndex}.redirectAfterPurchaseEnabled`}
                    render={({field}) => (
                      <Switch
                        checked={Boolean(field.value)}
                        onChange={(checked) => {
                          field.onChange(checked);
                          if (!checked) {
                            setValue(
                              `tiers.${tierIndex}.redirectAfterPurchase`,
                              undefined,
                              {shouldDirty: true}
                            );
                            unregister(`tiers.${tierIndex}.telegramGroupId`);
                            unregister(`tiers.${tierIndex}.urlRedirect`);
                          }
                        }}
                      />
                    )}
                  />
                </div>
                {redirectAfterPurchaseEnabled ? (
                  <TierRedirectAfterPurchase tierIndex={tierIndex} />
                ) : null}
              </div>
            </Space>
          </div>

          <TierSummaryText tierIndex={tierIndex} />
        </div>
      ) : null}
    </div>
  );
}
